import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSectionNavigation = (sectionId) => {
    if (isHomePage) {
      // If already on home page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page and then scroll to section
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const navItems = [
    { name: 'Features', sectionId: 'features', isSection: true },
    { name: 'Screenshots', sectionId: 'screenshots', isSection: true },
    { name: 'How it Works', sectionId: 'how-it-works', isSection: true },
    { name: 'Pricing', sectionId: 'pricing', isSection: true, hidden: true },
    { name: 'Download', sectionId: 'download', isSection: true },
    { name: 'About', sectionId: 'about', isSection: true },
    { name: 'Guide', href: '/guide', isRoute: true },
    { name: 'Releases', href: '/releases', isRoute: true },
    { name: 'MCP Servers', href: '/mcp-servers', isRoute: true },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: 'Contact', href: '/contact', isRoute: true },
  ];

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'border-b border-white/10' 
          : 'bg-transparent'
      }`}
      style={isScrolled ? {
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(60px)',
        WebkitBackdropFilter: 'blur(60px)'
      } : {}}
    >
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg bg-gradient-to-r from-secondary-500 to-accent-500">
                <img 
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  alt="Bervin AI" 
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML = '<span class="text-white font-bold text-xl font-display flex items-center justify-center w-full h-full">B</span>';
                  }}
                />
              </div>
              <span className="text-2xl font-display font-bold gradient-text">
                Bervin
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.filter((item) => !item.hidden).map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.3 }}
                whileHover={{ y: -2 }}
                className="relative group"
              >
                {item.isRoute ? (
                  <Link
                    to={item.href}
                    onClick={() => {
                      // Force immediate scroll to top with multiple fallbacks
                      setTimeout(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                        document.documentElement.scrollTop = 0;
                        document.body.scrollTop = 0;
                        if (window.pageYOffset !== 0) {
                          window.scroll(0, 0);
                        }
                      }, 0);
                    }}
                    className={`text-gray-300 hover:text-white transition-colors duration-300 ${
                      location.pathname === item.href ? 'text-accent-400' : ''
                    }`}
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary-500 to-accent-500 group-hover:w-full transition-all duration-300"></span>
                  </Link>
                ) : item.isSection ? (
                  <button
                    onClick={() => handleSectionNavigation(item.sectionId)}
                    className="text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary-500 to-accent-500 group-hover:w-full transition-all duration-300"></span>
                  </button>
                ) : (
                  <a
                    href={item.href}
                    className="text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary-500 to-accent-500 group-hover:w-full transition-all duration-300"></span>
                  </a>
                )}
              </motion.div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center">
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(168, 85, 247, 0.5)" }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.preventDefault();
                try {
                  window.open('https://bervin.appiworks.com/app', '_blank', 'noopener,noreferrer');
                } catch (error) {
                  window.location.href = 'https://bervin.appiworks.com/app';
                }
              }}
              className="px-6 py-2 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-lg font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              Try Now
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            title={isMobileMenuOpen ? "Close menu" : "Open menu"}
            className="md:hidden p-2 rounded-lg border border-white/20 text-gray-300"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(40px)',
              WebkitBackdropFilter: 'blur(40px)'
            }}
          >
            {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ 
            opacity: isMobileMenuOpen ? 1 : 0, 
            height: isMobileMenuOpen ? 'auto' : 0 
          }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden border-t border-white/10"
          style={{
            background: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(60px)',
            WebkitBackdropFilter: 'blur(60px)'
          }}
        >
          <div className="pt-4 pb-2 space-y-2">
            {navItems.filter((item) => !item.hidden).map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ 
                  opacity: isMobileMenuOpen ? 1 : 0, 
                  x: isMobileMenuOpen ? 0 : -20 
                }}
                transition={{ delay: index * 0.1 }}
              >
                {item.isRoute ? (
                  <Link
                    to={item.href}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      // Force immediate scroll to top with multiple fallbacks
                      setTimeout(() => {
                        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                        document.documentElement.scrollTop = 0;
                        document.body.scrollTop = 0;
                        if (window.pageYOffset !== 0) {
                          window.scroll(0, 0);
                        }
                      }, 0);
                    }}
                    className={`block py-2 px-4 text-gray-300 hover:text-white transition-colors duration-300 ${
                      location.pathname === item.href ? 'text-accent-400' : ''
                    }`}
                  >
                    {item.name}
                  </Link>
                ) : item.isSection ? (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleSectionNavigation(item.sectionId);
                    }}
                    className="block py-2 px-4 text-gray-300 hover:text-white transition-colors duration-300 w-full text-left"
                  >
                    {item.name}
                  </button>
                ) : (
                  <a
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-2 px-4 text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {item.name}
                  </a>
                )}
              </motion.div>
            ))}
            
            <div className="flex justify-center pt-4 px-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault();
                  try {
                    window.open('https://bervin.appiworks.com/app', '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    window.location.href = 'https://bervin.appiworks.com/app';
                  }
                }}
                className="px-6 py-2 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-lg font-semibold text-white cursor-pointer"
              >
                Try Now
              </motion.button>
            </div>
          </div>
        </motion.div>
      </nav>
    </motion.header>
  );
};

export default Header;
